import type { ReactElement } from 'react'
import { IconSwap } from './icons/IconSwap'
import styles from './SwapButton.module.css'

export type SwapButtonVariant = 'phone' | 'tablet'

export interface SwapButtonProps {
  /** Called when the user clicks the swap control. Optional — when omitted the button is rendered but inert. */
  readonly onClick?: () => void
  /**
   * Layout variant — drives button size.
   * - `'phone'`: 42×42
   * - `'tablet'`: 52×52
   *
   * @defaultValue `'tablet'`
   */
  readonly variant?: SwapButtonVariant
  /** Accessible label. @defaultValue `'Swap From and To'` */
  readonly ariaLabel?: string
}

/**
 * Round button rendering the swap-arrows glyph for swapping the From↔To
 * stations of a RouteCard selection.
 *
 * The icon ({@link IconSwap}) uses `stroke="currentColor"` so it inherits
 * the button's `color` and adapts to light/dark themes via the shared
 * `--mm-color-text-secondary` token.
 *
 * Source-of-truth icon asset:
 * `metro/docs/superpowers/specs/assets/routecard-v2/icon-swap.svg`
 * (frame 36×37, stroke 1.1636).
 */
export function SwapButton({
  onClick,
  variant = 'tablet',
  ariaLabel = 'Swap From and To',
}: SwapButtonProps = {}): ReactElement {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      className={`${styles.button} ${styles[variant]}`}
      data-testid="route-card-swap"
      data-variant={variant}
      onClick={onClick}
    >
      <IconSwap className={styles.icon} width="36" height="37" />
    </button>
  )
}
